import Axios, { AxiosError } from "axios";
import { handleUnauthenticatedUser } from "src/App";

const BASE_URL = "/api/Instamart/InternalProcess/paymentAutomation";

export type PayStatus = "releasing" | "held_full_hold" | "held_gst" | "zero_net";

export interface RunSummary {
  runId: string;
  generatedAt: string;
  generatedBy: number;
  dueDateBasis: string;
  dueDateCutoff: string;
  bdpoBatchId: string | null;
  sorBatchId: string | null;
  panCount: number;
  releasingCount: number;
  heldCount: number;
  totalAmountToPay: number;
  totalNetDue: number;
}

export interface Kpis {
  vendorsInRun: number;
  releasing: number;
  amountToPay: number;
  held: number;
  netDue: number;
}

export interface PanRow {
  pan: string;
  vendorName: string | null;
  isBdpo: boolean;
  status: PayStatus;
  panFlag: string | null;
  booksCb: number;
  bdpoAmount: number;
  bdpoReferenceAmount: number;
  paymentInProgress: number;
  adjustedCb: number;
  dueInvoices: number;
  creditToAdjust: number;
  entityHold: number;
  otherHold: number;
  netDue: number;
  positiveReleasable: number;
  negativeReleasable: number;
  grandTotal: number;
  amountToPay: number;
  entityCount: number;
  contributingEntityCount: number;
  zeroedEntityCount: number;
}

export interface EntityRow {
  pan: string;
  entityName: string;
  booksCb: number;
  bdpoAmount: number;
  paymentInProgress: number;
  adjustedCb: number;
  dueInvoices: number;
  creditToAdjust: number;
  entityHold: number;
  otherHold: number;
  netDue: number;
  releasable: number;
  holdFlag: string | null;
}

export interface ReportStatus {
  status: "Generating Report" | "Generated" | "Not Generated";
  lastGeneratedAt: string | null;
  dueDateBasis: string | null;
}

export interface ResultsData {
  run: RunSummary | null;
  kpis: Kpis;
  rows: PanRow[];
  totalRows: number;
}

export interface BdpoList {
  batchId: string | null;
  uploadedAt: string | null;
  uploadedBy: number | null;
  sourceFilename: string | null;
  partyCount: number;
  activeCount: number;
  parties: Array<{ pan: string; name: string | null; isActive: boolean }>;
}

export interface SorList {
  batchId: string | null;
  uploadedAt: string | null;
  uploadedBy: number | null;
  sourceFilename: string | null;
  codeCount: number;
  codes: Array<{ pan: string; vendorCode: string }>;
}

export interface ResultsQuery {
  status?: string;
  entityName?: string[];
  search?: string;
  sortKey?: string;
  sortDir?: "ASC" | "DESC";
  limit?: number;
  offset?: number;
}

/** Pulls the human-readable message out of the backend error envelope. */
export const apiErrorMessage = async (error: unknown, fallback: string): Promise<string> => {
  const err = error as AxiosError<any>;
  let data: any = err?.response?.data;
  // blob/arraybuffer responses carry the JSON envelope as bytes
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = null;
    }
  } else if (data instanceof ArrayBuffer) {
    try {
      data = JSON.parse(new TextDecoder().decode(data));
    } catch {
      data = null;
    }
  }
  return data?.message || fallback;
};

const cleanParams = (q: ResultsQuery) => {
  const p: Record<string, string | number | string[]> = {};
  Object.entries(q).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v) && v.length === 0) return;
    p[k] = v as string | number | string[];
  });
  return p;
};

// Arrays are sent as repeated keys (entityName=A&entityName=B), not entityName[]=A
const serializeParams = (params: Record<string, unknown>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach((item) => usp.append(k, String(item)));
    else if (v !== undefined && v !== null) usp.append(k, String(v));
  });
  return usp.toString();
};

const guard = async <T>(call: Promise<{ data: { data: T } }>): Promise<T> => {
  try {
    return (await call).data.data;
  } catch (error) {
    handleUnauthenticatedUser(error as AxiosError);
    throw error;
  }
};

export const paymentAutomationApi = {
  generate: (dueDateBasis: string) => Axios.post(`${BASE_URL}/generate`, { dueDateBasis }),
  status: () => guard<ReportStatus>(Axios.get(`${BASE_URL}/status`)),
  results: (q: ResultsQuery) =>
    guard<ResultsData>(Axios.get(`${BASE_URL}/results`, { params: cleanParams(q), paramsSerializer: serializeParams })),
  panDetail: (pan: string) =>
    guard<{ pan: PanRow; entities: EntityRow[] }>(Axios.get(`${BASE_URL}/results/${encodeURIComponent(pan)}`)),
  bdpo: () => guard<BdpoList>(Axios.get(`${BASE_URL}/bdpo`)),
  sor: () => guard<SorList>(Axios.get(`${BASE_URL}/sor`)),
  uploadBdpo: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return guard<{ batchId: string; partyCount: number; activeCount: number }>(
      Axios.post(`${BASE_URL}/bdpo/upload`, fd)
    );
  },
  uploadSor: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return guard<{ batchId: string; codeCount: number; deactivatedBatchId: string | null }>(
      Axios.post(`${BASE_URL}/sor/upload`, fd)
    );
  },
  /** Downloads one of the Excel exports and triggers a browser save. */
  download: async (kind: "treasury" | "detailed", q: ResultsQuery) => {
    const { limit, offset, ...filters } = q;
    void limit;
    void offset;
    const path = kind === "treasury" ? "/download" : "/download/detailed";
    const response = await Axios.get(`${BASE_URL}${path}`, {
      params: cleanParams(filters),
      paramsSerializer: serializeParams,
      responseType: "blob",
    });
    const disposition: string = response.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const fileName = match
      ? decodeURIComponent(match[1])
      : kind === "treasury"
      ? "payment-file.xlsx"
      : "payment-file-detailed.xlsx";
    const url = URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
